import { Fragment, useEffect, useState, useRef, useCallback } from "react";
import showAlert from "../../../server/ShowAlert";
import { BaseUrl } from "../../../../axiosInstance";
import { useSelector } from "react-redux";
import { GetMosadDetails } from "../../../server/Payment";

const TOKEF_PATTERN = /^\d{2}\/\d{2}$/;
const CARD_CHECK_INTERVAL_MS = 700;
const CARD_VALIDATE_TIMEOUT_MS = 1500;
const NEDARIM_ORIGIN = "https://www.matara.pro";

const formatTokef = (raw) => {
  let value = String(raw || "")
    .replace(/[^0-9]/g, "")
    .slice(0, 4);
  if (value.length > 2) {
    value = `${value.slice(0, 2)}/${value.slice(2)}`;
  }
  return value;
};

const isTokefValid = (value) => {
  const v = value || "";
  if (!TOKEF_PATTERN.test(v)) return false;
  const [mm] = v.split("/");
  const month = Number(mm);
  return month >= 1 && month <= 12;
};

/** Nedarim ValidateFields checks Card first, then Tokef, then CVV.
 * With Tokef/CVV hidden, a filled card yields Field "Expiration" (empty Tokef).
 * Empty/invalid card yields Field "Card". */
const isCardNumberAccepted = (data) => {
  if (!data || data.Name !== "ValidateFields") return null;
  if (data.Field === "Card") return false;
  if (data.Value === "OK") return true;
  // Card passed; next failure is on hidden Tokef/CVV fields.
  if (data.Field === "Expiration" || data.Field === "CVV") return true;
  return null;
};

const PayNedarim = ({
  save,
  total,
  ClosePayPopup,
  numPayments,
  numPaymentSelect,
  orderIdInNedarim,
  pay,
  handleDebitTokenPayment,
}) => {
  const user = useSelector((state) => state.user.user);
  const iframeRef = useRef(null);
  const [isPaymentInProgress, setIsPaymentInProgress] = useState(false);
  const isPaymentBlockedRef = useRef(false);
  const hasDispatchedDebitRef = useRef(false);
  const [, setToken] = useState(null);
  const [tokef, setTokef] = useState("");
  const [cardReady, setCardReady] = useState(false);
  const [mosad, setMosad] = useState("");
  const [apiValid, setApiValid] = useState("");
  const handlePaymentRef = useRef(handleDebitTokenPayment);
  const closePayPopupRef = useRef(ClosePayPopup);
  const tokefRef = useRef("");

  useEffect(() => {
    handlePaymentRef.current = handleDebitTokenPayment;
  }, [handleDebitTokenPayment]);

  useEffect(() => {
    closePayPopupRef.current = ClosePayPopup;
  }, [ClosePayPopup]);

  useEffect(() => {
    tokefRef.current = tokef;
  }, [tokef]);

  useEffect(() => {
    GetMosadDetails()
      .then((response) => {
        if (response.data) {
          setMosad(response.data["Mosad"]);
          setApiValid(response.data["ApiValid"]);
        }
      })
      .catch((error) => {
        console.error("Error fetching Mosad details:", error);
      });
  }, []);

  const setTokenAsync = useCallback((value) => {
    return new Promise((resolve) => {
      setToken(value);
      resolve(value);
    });
  }, []);

  const PostNedarim = useCallback((Data) => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(Data, NEDARIM_ORIGIN);
      return true;
    }
    return false;
  }, []);

  const requestCardValidation = useCallback(() => {
    PostNedarim({ Name: "ValidateFields" });
  }, [PostNedarim]);

  const confirmCardNumberFilled = useCallback(() => {
    return new Promise((resolve) => {
      let settled = false;

      const finish = (ok) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        window.removeEventListener("message", onMessage);
        resolve(ok);
      };

      const onMessage = (e) => {
        if (e.origin !== NEDARIM_ORIGIN || !e.data) return;
        const accepted = isCardNumberAccepted(e.data);
        if (accepted === null) return;
        setCardReady(accepted);
        finish(accepted);
      };

      const timeoutId = setTimeout(
        () => finish(false),
        CARD_VALIDATE_TIMEOUT_MS,
      );

      window.addEventListener("message", onMessage);
      if (!PostNedarim({ Name: "ValidateFields" })) {
        finish(false);
      }
    });
  }, [PostNedarim]);

  useEffect(() => {
    const receivingMessages = async (e) => {
      if (e.origin !== NEDARIM_ORIGIN || !e.data) return;

      const accepted = isCardNumberAccepted(e.data);
      if (accepted !== null) {
        setCardReady(accepted);
        return;
      }

      const status = e.data.Value?.Status;
      if (typeof status !== "string") return;

      switch (status) {
        case "OK":
          if (e.data.Value.Token) {
            if (hasDispatchedDebitRef.current) return;
            hasDispatchedDebitRef.current = true;
            const newToken = await setTokenAsync(e.data.Value.Token);
            handlePaymentRef.current(newToken, total, tokefRef.current);
          }
          break;
        case "Error":
          if (hasDispatchedDebitRef.current) return;
          showAlert({
            msgTitle: "העסקה נכשלה",
            msgText: `שגיאה: ${e.data.Value.Message}`,
            msgType: "error",
          });
          isPaymentBlockedRef.current = false;
          setIsPaymentInProgress(false);
          closePayPopupRef.current(false);
          break;
        default:
          console.warn("Unexpected message received:", e.data);
          isPaymentBlockedRef.current = false;
          setIsPaymentInProgress(false);
      }
    };

    window.addEventListener("message", receivingMessages);
    return () => {
      window.removeEventListener("message", receivingMessages);
    };
  }, [setTokenAsync, total]);

  // Keep checking the iframe card field (cross-origin) so clearing the number
  // disables the button again.
  useEffect(() => {
    requestCardValidation();
    const intervalId = setInterval(
      requestCardValidation,
      CARD_CHECK_INTERVAL_MS,
    );
    return () => clearInterval(intervalId);
  }, [requestCardValidation]);

  const canPay =
    Boolean(mosad && apiValid) &&
    cardReady &&
    isTokefValid(tokef) &&
    !isPaymentInProgress;

  const handlePayment = async () => {
    if (!canPay || isPaymentBlockedRef.current) return;
    if (!isTokefValid(tokef)) return;

    // Final hard check: do not start payment without a filled card number.
    const hasCardNumber = await confirmCardNumberFilled();
    if (!hasCardNumber) {
      setCardReady(false);
      showAlert({
        msgTitle: "חסר מספר כרטיס",
        msgText: "יש להזין מספר כרטיס אשראי לפני התשלום",
        msgType: "error",
      });
      return;
    }

    isPaymentBlockedRef.current = true;
    setIsPaymentInProgress(true);

    const posted = PostNedarim({
      Name: "FinishTransaction2",
      Value: {
        Mosad: mosad,
        ApiValid: apiValid,
        Zeout: user.Id,
        FirstName: user.Name,
        LastName: user.LastName,
        City: user.City,
        Phone: user.Phone,
        Mail: user.Email,
        PaymentType: "CreateToken",
        Amount: total,
        Tashlumim: numPaymentSelect,
        Currency: "1",
        Groupe: "",
        Comment: "ללא כתובת למשלוח!",
        Param1: "",
        // Nedarim rejects Tokef/CVV in CreateToken mode; the expiry goes only
        // to the debit-by-token backend call (handleDebitTokenPayment).
        CallBack: `${BaseUrl}`,
      },
    });

    if (!posted) {
      showAlert({
        msgTitle: "Error",
        msgText: "Could not load payment iframe.",
        msgType: "error",
      });
      isPaymentBlockedRef.current = false;
      setIsPaymentInProgress(false);
    }
  };

  const handleTokefChange = (e) => {
    setTokef(formatTokef(e.target.value));
  };

  return (
    <Fragment>
      <div className="payment-form">
        {/* Card number — Nedarim iframe (user types directly; gateway tokenises) */}
        <div className="field">
          <label>מספר כרטיס אשראי</label>
          <div className="pn-iframe-wrap">
            <iframe
              id="NedarimFrame"
              ref={iframeRef}
              title="pay"
              scrolling="no"
              src={`${NEDARIM_ORIGIN}/nedarimplus/iframe?Tokef=Hide&CVV=Hide&Picture=Hide&retdigits=1&HideCCLabel=1`}
              onLoad={requestCardValidation}
            />
          </div>
        </div>

        {/* Expiry */}
        <div className="field field--tokef">
          <label>תוקף כרטיס אשראי</label>
          <input
            className="textBox pn-input"
            type="text"
            inputMode="numeric"
            value={tokef}
            onChange={handleTokefChange}
            placeholder="MM/YY"
            maxLength="5"
            autoComplete="cc-exp"
            dir="ltr"
          />
        </div>
      </div>
      <div className="btn-wrapper">
        <button
          type="button"
          className={`paymentBtn${
            isPaymentInProgress
              ? " disabled"
              : canPay
                ? ""
                : " paymentBtn--incomplete"
          }`}
          onClick={handlePayment}
          disabled={!canPay}
          aria-disabled={!canPay}
        >
          {isPaymentInProgress ? "ממתין..." : "תשלום"}
        </button>
      </div>
    </Fragment>
  );
};

export default PayNedarim;
